import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  TimePicker,
  Switch,
  Space,
  Tag,
  Typography,
  message,
  Popconfirm,
  Row,
  Col,
  DatePicker,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  FileTextOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface ScheduledReport {
  _id: string;
  name: string;
  reportType: 'dashboard' | 'financial' | 'campaign' | 'customer' | 'inventory' | 'custom';
  frequency: 'daily' | 'weekly' | 'monthly';
  dayOfWeek?: number;
  dayOfMonth?: number;
  time: string;
  recipients: Array<{ email: string; name?: string }>;
  format: 'pdf' | 'excel' | 'both';
  filters?: {
    stationIds?: string[];
    dateRange?: {
      startDate?: string;
      endDate?: string;
    };
  };
  isActive: boolean;
  lastSentAt?: string;
  nextSendAt?: string;
  totalSent: number;
}

const ScheduledReports: React.FC = () => {
  const [reports, setReports] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchReports();
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await stationOwnerAPI.getScheduledReports();
      setReports(data);
    } catch (error: any) {
      message.error('Raporlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReport = () => {
    setEditingReport(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditReport = (report: ScheduledReport) => {
    setEditingReport(report);
    form.setFieldsValue({
      ...report,
      time: dayjs(report.time, 'HH:mm'),
      dateRange: report.filters?.dateRange?.startDate && report.filters?.dateRange?.endDate
        ? [dayjs(report.filters.dateRange.startDate), dayjs(report.filters.dateRange.endDate)]
        : null
    });
    setModalVisible(true);
  };

  const handleDeleteReport = async (id: string) => {
    try {
      await stationOwnerAPI.deleteScheduledReport(id);
      message.success('Rapor silindi');
      fetchReports();
    } catch (error: any) {
      message.error('Rapor silinemedi');
    }
  };

  const handleSendNow = async (id: string) => {
    try {
      await stationOwnerAPI.sendReportNow(id);
      message.success('Rapor gönderildi');
      fetchReports();
    } catch (error: any) {
      message.error('Rapor gönderilemedi');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const reportData = {
        ...values,
        time: values.time.format('HH:mm'),
        filters: {
          dateRange: values.dateRange ? {
            startDate: values.dateRange[0].toDate(),
            endDate: values.dateRange[1].toDate()
          } : undefined
        }
      };

      if (editingReport) {
        await stationOwnerAPI.updateScheduledReport(editingReport._id, reportData);
        message.success('Rapor güncellendi');
      } else {
        await stationOwnerAPI.createScheduledReport(reportData);
        message.success('Rapor oluşturuldu');
      }

      setModalVisible(false);
      fetchReports();
    } catch (error: any) {
      message.error(editingReport ? 'Rapor güncellenemedi' : 'Rapor oluşturulamadı');
    }
  };

  const columns = [
    {
      title: 'Rapor Adı',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Rapor Tipi',
      dataIndex: 'reportType',
      key: 'reportType',
      render: (type: string) => {
        const types: Record<string, string> = {
          dashboard: 'Dashboard',
          financial: 'Finansal',
          campaign: 'Kampanya',
          customer: 'Müşteri',
          inventory: 'Envanter',
          custom: 'Özel'
        };
        return <Tag color="blue">{types[type] || type}</Tag>;
      }
    },
    {
      title: 'Sıklık',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (freq: string, record: ScheduledReport) => {
        const frequencies: Record<string, string> = {
          daily: 'Günlük',
          weekly: `Haftalık (${['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'][record.dayOfWeek || 0]})`,
          monthly: `Aylık (${record.dayOfMonth || 1}. gün)`
        };
        return <Tag color="green">{frequencies[freq] || freq}</Tag>;
      }
    },
    {
      title: 'Saat',
      dataIndex: 'time',
      key: 'time',
      render: (time: string) => (
        <Space>
          <ClockCircleOutlined />
          <span>{time}</span>
        </Space>
      )
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      render: (format: string) => {
        const formats: Record<string, string> = {
          pdf: 'PDF',
          excel: 'Excel',
          both: 'PDF + Excel'
        };
        return <Tag>{formats[format] || format}</Tag>;
      }
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean) => (
        <Tag color={active ? 'success' : 'default'}>
          {active ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'Son Gönderim',
      dataIndex: 'lastSentAt',
      key: 'lastSentAt',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-'
    },
    {
      title: 'Toplam Gönderim',
      dataIndex: 'totalSent',
      key: 'totalSent',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ScheduledReport) => (
        <Space>
          <Button
            type="link"
            icon={<SendOutlined />}
            onClick={() => handleSendNow(record._id)}
            size="small"
          >
            Gönder
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditReport(record)}
            size="small"
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu raporu silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteReport(record._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            Otomatik Raporlar
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            Zamanlanmış raporlarınızı yönetin
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddReport}
            block={isMobile}
          >
            Yeni Rapor Oluştur
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={reports}
          loading={loading}
          rowKey="_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingReport ? 'Raporu Düzenle' : 'Yeni Rapor Oluştur'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 800}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            frequency: 'daily',
            format: 'pdf',
            isActive: true,
            time: dayjs('09:00', 'HH:mm'),
            recipients: [{ email: '' }]
          }}
        >
          <Form.Item
            name="name"
            label="Rapor Adı"
            rules={[{ required: true, message: 'Rapor adı gerekli' }]}
          >
            <Input placeholder="Örn: Aylık Finansal Rapor" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="reportType"
                label="Rapor Tipi"
                rules={[{ required: true, message: 'Rapor tipi seçin' }]}
              >
                <Select placeholder="Rapor tipi seçin">
                  <Option value="dashboard">Dashboard</Option>
                  <Option value="financial">Finansal</Option>
                  <Option value="campaign">Kampanya</Option>
                  <Option value="customer">Müşteri</Option>
                  <Option value="inventory">Envanter</Option>
                  <Option value="custom">Özel</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="frequency"
                label="Sıklık"
                rules={[{ required: true, message: 'Sıklık seçin' }]}
              >
                <Select placeholder="Sıklık seçin">
                  <Option value="daily">Günlük</Option>
                  <Option value="weekly">Haftalık</Option>
                  <Option value="monthly">Aylık</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.frequency !== currentValues.frequency}
          >
            {({ getFieldValue }) => {
              const frequency = getFieldValue('frequency');
              return (
                <>
                  {frequency === 'weekly' && (
                    <Form.Item
                      name="dayOfWeek"
                      label="Haftanın Günü"
                      rules={[{ required: true, message: 'Gün seçin' }]}
                    >
                      <Select placeholder="Gün seçin">
                        <Option value={0}>Pazar</Option>
                        <Option value={1}>Pazartesi</Option>
                        <Option value={2}>Salı</Option>
                        <Option value={3}>Çarşamba</Option>
                        <Option value={4}>Perşembe</Option>
                        <Option value={5}>Cuma</Option>
                        <Option value={6}>Cumartesi</Option>
                      </Select>
                    </Form.Item>
                  )}

                  {frequency === 'monthly' && (
                    <Form.Item
                      name="dayOfMonth"
                      label="Ayın Günü"
                      rules={[{ required: true, message: 'Gün seçin' }]}
                    >
                      <InputNumber min={1} max={31} placeholder="1-31 arası" style={{ width: '100%' }} />
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="time"
                label="Gönderim Saati"
                rules={[{ required: true, message: 'Saat seçin' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="format"
                label="Format"
                rules={[{ required: true, message: 'Format seçin' }]}
              >
                <Select placeholder="Format seçin">
                  <Option value="pdf">PDF</Option>
                  <Option value="excel">Excel</Option>
                  <Option value="both">PDF + Excel</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="recipients"
            label="Alıcılar"
            rules={[{ required: true, message: 'En az bir alıcı ekleyin' }]}
          >
            <Form.List name="recipients">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        {...field}
                        name={[field.name, 'email']}
                        rules={[{ required: true, type: 'email', message: 'Geçerli e-posta girin' }]}
                      >
                        <Input placeholder="E-posta adresi" style={{ width: isMobile ? '100%' : 300 }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button onClick={() => remove(field.name)} danger>
                          Kaldır
                        </Button>
                      )}
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Alıcı Ekle
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Tarih Aralığı (Opsiyonel)"
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isActive"
            valuePropName="checked"
            label="Aktif"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingReport ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduledReports;

